import React from "react";

// ── Type badge styles ─────────────────────────────────────────────────────────

const EMPLOYEE_TYPE_STYLE = {
  Contractor: { bg: "#fff7ed", color: "#c2500a", border: "#fed7aa" },
  Employee: { bg: "#f0fdf4", color: "#15803d", border: "#bbf7d0" },
  FTE: { bg: "#f0fdf4", color: "#15803d", border: "#bbf7d0" },
};

const USER_TYPE_STYLE = {
  Member: { bg: "#f0f9e6", color: "#4d7a10", border: "#c7e68a" },
  Guest: { bg: "#fdf4ff", color: "#7c3aed", border: "#e9d5ff" },
};

const Badge = ({ label, style = {} }) => (
  <span
    style={{
      fontSize: 11,
      fontWeight: 700,
      padding: "2px 10px",
      borderRadius: 20,
      background: style.bg || "#f1f5f9",
      color: style.color || "#64748b",
      border: `1px solid ${style.border || "#e2e8f0"}`,
      whiteSpace: "nowrap",
    }}
  >
    {label}
  </span>
);

// ── Main component ────────────────────────────────────────────────────────────

const ADSection = ({ user }) => {
  // API shape: { email, exists_in_ad, details: { ... } }
  const d = user.details || {};

  // Initials: first two chars of givenName, or first two of mailNickname
  const initials = d.givenName
    ? d.givenName.slice(0, 2).toUpperCase()
    : (d.mailNickname || "?").slice(0, 2).toUpperCase();

  // Job title — may be comma-separated e.g. "External,DTTS MF CLOUD ENGIN"
  const jobTitleParts = d.jobTitle
    ? d.jobTitle.split(",").map((p) => p.trim()).filter(Boolean)
    : [];

  // Account creation date
  const createdDate = d.createdDateTime
    ? new Date(d.createdDateTime).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    })
    : null;

  const fields = [
    { label: "Email", value: d.mail },
    // Only show UPN if it differs from mail
    d.userPrincipalName !== d.mail
      ? { label: "UPN", value: d.userPrincipalName }
      : null,
    { label: "Mail Nickname", value: d.mailNickname },
    { label: "Country", value: d.country },
    { label: "Employee Type", value: d.employeeType },
    { label: "User Type", value: d.userType },
    { label: "Account Created", value: createdDate },
    { label: "Object ID", value: d.id },
  ].filter(Boolean).filter((f) => f.value);

  return (
    <div className="ecr-us__section-body">
      {/* ── Header ── */}
      <div className="ecr-us__ad-header">
        <div className="ecr-us__ad-avatar">{initials}</div>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
            <h3 className="ecr-us__ad-name" style={{ margin: 0 }}>
              {d.givenName || d.mailNickname || "—"}
            </h3>
            {d.employeeType && (
              <Badge
                label={d.employeeType}
                style={EMPLOYEE_TYPE_STYLE[d.employeeType] || {}}
              />
            )}
            {d.userType && (
              <Badge
                label={d.userType}
                style={USER_TYPE_STYLE[d.userType] || {}}
              />
            )}
          </div>
          {jobTitleParts.length > 0 && (
            <p className="ecr-us__ad-title" style={{ margin: 0 }}>
              {jobTitleParts.join(" · ")}
            </p>
          )}
        </div>
      </div>

      {/* ── Meta grid ── */}
      <div className="ecr-us__meta-row" style={{ marginTop: 16 }}>
        {fields.map(({ label, value }) => (
          <div key={label} className="ecr-us__meta-item">
            <span className="ecr-us__meta-label">{label}</span>
            <span className="ecr-us__meta-value">{value}</span>
          </div>
        ))}
      </div>

      {/* ── Other mails ── */}
      {d.otherMails?.length > 0 && (
        <div style={{ marginTop: 4 }}>
          <span className="ecr-us__meta-label">Other Mails</span>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
            {d.otherMails.map((m) => (
              <span
                key={m}
                style={{
                  fontSize: 12,
                  fontWeight: 500,
                  background: "#f1f5f9",
                  color: "#334155",
                  border: "1px solid #e2e8f0",
                  borderRadius: 6,
                  padding: "3px 10px",
                }}
              >
                {m}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ADSection;
